import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { Modal } from "@/shared/ui/modal"
import { yupResolver } from "@hookform/resolvers/yup"
import { SubmitHandler, useForm } from "react-hook-form"
import { FormInput } from "@/shared/ui/form-input"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { fromNanoToUnit, selectBonuses, toNano } from "@/entities/bonus"
import { useEffect, useMemo, useState } from "react"
import BonusSvg from '@/shared/assets/images/BonusMenuIcon.svg'
import { selectMainWallet } from "@/entities/community"
import TransferSvg from '@/shared/assets/images/ArrowsLeftRight.svg'
import { closeFoundationAccrualModal, fetchFoundationAccrual, foundationAccrualValidationSchema, selectIsFoundationAccrualModalOpen, selectOnAccrual, selectSelectedFoundationAccrualProgram } from "../../model"
import { getSocialPrograms, ISocialProgram } from "@/shared/api/social-programs"

interface FoundationAccrualFormValues {
    program_id: string,
    bonus_id: string,
    amount: number,
    comment: string
}

const initialState: FoundationAccrualFormValues = {
    program_id: "",
    bonus_id: "",
    amount: 0,
    comment: ""
}

export const FoundationAccrualModal = () => {
    const dispatch = useAppDispatch()
    const [programs, setPrograms] = useState<ISocialProgram[]>([])
    const isOpen = useAppSelector(selectIsFoundationAccrualModalOpen)
    const onAccrual = useAppSelector(selectOnAccrual)
    const selectedProgram = useAppSelector(selectSelectedFoundationAccrualProgram)
    const bonuses = useAppSelector(selectBonuses)
    const wallet = useAppSelector(selectMainWallet)
    const {
        register,
        handleSubmit,
        control,
        watch,
        formState: { errors, isSubmitting },
        reset,
        getValues,
    } = useForm({
        defaultValues: initialState,
        resolver: yupResolver(foundationAccrualValidationSchema)
    })

    const bonusSelectOptions = useMemo(() => bonuses.map((bonus) => ({
        value: bonus.id,
        label: bonus.name
    })), [bonuses])

    const programSelectOptions = useMemo(() => programs.map((program) => ({
        value: program.id,
        label: program.name
    })), [programs])

    const handleClose = () => {
        reset(initialState)
        dispatch(closeFoundationAccrualModal())
    }

    const calculateAccrual = useMemo(() => {
        const walletBalance = wallet[getValues('bonus_id')] ?? 0
        const isEnought = (walletBalance > 0) && (walletBalance >= toNano(getValues('amount')))
        const afterAccrual = walletBalance - toNano(getValues('amount'))
        return { isEnought, afterAccrual }
    }, [watch('amount'), watch('bonus_id')])

    const onSubmit: SubmitHandler<typeof initialState> = async (data) => {
        dispatch(fetchFoundationAccrual({
            program_id: data.program_id,
            bonus_id: data.bonus_id,
            amount_nano: toNano(data.amount),
            comment: data.comment
        }))
            .unwrap()
            .then(() => {
                notify.success('Бонусы успешно начислены')
                reset()
                onAccrual?.()
                handleClose()
            })
            .catch((error) => {
                notify.error(getErrorMessage(error))
            })
    }

    useEffect(() => {
        if (isOpen) {
            if (selectedProgram) {
                reset({ ...initialState, program_id: selectedProgram.id })
                return
            }

            getSocialPrograms('foundation').then(res => setPrograms(res.data)).catch(() => {})
        }
    }, [isOpen, selectedProgram, reset])

    return (
        <Modal
            isOpen={isOpen}
            title={(
                <div>
                    Пожертвование
                    <br />
                    благотворительному
                    <br />
                    фонду
                </div>
            )}
            onRequestClose={handleClose}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                {selectedProgram ? (
                    <>
                        <input type="hidden" {...register('program_id')} />
                        <div className="flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6]">
                            <label className="font-semibold uppercase text-base text-[#828282]">
                                Программа-фонд
                            </label>
                            <div className="text-lg text-color-dark">
                                {selectedProgram.name}
                            </div>
                        </div>
                    </>
                ) : (
                    <FormInput
                        label={"Программа-фонд"}
                        id={"program_id"}
                        options={programSelectOptions}
                        control={control}
                        error={errors['program_id']}
                        type="select"
                        required={true}
                        placeholder="Выберите программу"
                    />
                )}
                <FormInput
                    label={"Бонус"}
                    id={"bonus_id"}
                    options={bonusSelectOptions}
                    control={control}
                    error={errors['bonus_id']}
                    type="select"
                    required={true}
                    placeholder="Выберите бонус"
                    components={{
                        ValueContainer: (props) => {
                            return (
                                <>
                                    {props.hasValue &&
                                        <div className="w-8 h-8 flex justify-center mr-2 items-center rounded-full overflow-hidden">
                                            <img src={bonuses?.find((bonus) => bonus.id === props.getValue()?.[0]?.value)?.image} className="w-full object-contain h-full" />
                                        </div>
                                    }
                                    {props.children}
                                </>
                            )
                        }
                    }}
                />
                <FormInput
                    register={register}
                    label={"Количество бонусов"}
                    id={"amount"}
                    type="number"
                    error={errors['amount']}
                    required={true}
                    placeholder="Укажите количество бонусов"
                />
                <div className="mt-3 mb-1 flex justify-between items-center">
                    <div className="flex rounded-full bg-color-dark py-[6px] px-3 w-full items-center gap-[6px]">
                        <BonusSvg className="fill-color-dark text-color-primary" width={24} height={24} />
                        <div className="font-semibold text-white text-base">
                            {fromNanoToUnit(wallet[watch('bonus_id')] ?? 0)}
                        </div>
                    </div>
                    <TransferSvg width={32} height={32} className="mx-3" />
                    <div className={`flex rounded-full py-[6px] px-3 transition w-full items-center gap-[6px] ${calculateAccrual.isEnought ? 'bg-color-dark' : 'bg-color-danger'}`}>
                        <BonusSvg className={`transition ${calculateAccrual.isEnought ? 'text-color-primary fill-color-dark' : 'text-white fill-color-danger'}`} width={24} height={24} />
                        <div className="font-semibold text-white text-base">
                            {fromNanoToUnit(calculateAccrual.afterAccrual)}
                        </div>
                    </div>
                </div>
                <FormInput
                    register={register}
                    label={"Сообщение к начислению (опционально)"}
                    id={"comment"}
                    error={errors['comment']}
                    required={false}
                    placeholder="Введите ваше сообщение"
                />
                <div className="w-full flex gap-3">
                    <button onClick={handleClose} className="form_cancel_button">
                        Отменить
                    </button>
                    <button disabled={isSubmitting} className="form_handle_button" type="submit">
                        Начислить
                    </button>
                </div>
            </form>
        </Modal>
    )
}
